import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { MediaPreview } from "@/components/ui/MediaPreview/MediaPreview";
import type { MediaSpec } from "@/components/ui/MediaPreview/MediaPreview";
import { toPlatformKey, type PlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import { Field } from "@/components/auth/FormShell";
import { useIsMobile } from "@/lib/useMediaQuery";
import {
  mediaId,
  useBilling,
  useCreateMediaGenJob,
  useGenerateShotPrompt,
  useMedia,
  useMediaGenJob,
  useProducts,
  type MediaGenCapability,
} from "@/lib/queries";
import { MEDIA_MODELS } from "@notamen/core/media-models";
import type { MediaAspect, ShotMode } from "@notamen/core/types";
import shared from "./GenerateStudio.module.css";
import styles from "./GenerateMedia.module.css";

const CAPABILITIES: { value: MediaGenCapability; label: string }[] = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
];

const SHOT_MODES: { value: ShotMode; label: string; hint: string }[] = [
  { value: "composite", label: "Composite", hint: "Place your product into a generated scene" },
  { value: "reference", label: "Reference", hint: "Guide the look with your media as reference" },
  { value: "scene", label: "Scene", hint: "Generate a fresh scene from the prompt" },
];

const ASPECTS: MediaAspect[] = ["1:1", "4:5", "9:16", "16:9"];

/** Where the post will run. Each placement sets the frame (aspect) and feeds a
 *  composition hint to the AI shot director (`postType`). TikTok has a single
 *  vertical video feed — no Feed/Story/Reel split — so it's one honest option. */
type Placement = { id: string; label: string; aspect: MediaAspect };
// Keyed by PlatformKey ("ig"/"fb"/"tt") to match toPlatformKey() — the typed
// Record makes a key mismatch a compile error. The ids stay long (they carry
// the story/reel/feed/video substring the shot director keys its composition on).
const PLACEMENTS: Record<PlatformKey, Placement[]> = {
  ig: [
    { id: "instagram_feed", label: "Feed", aspect: "4:5" },
    { id: "instagram_story", label: "Story", aspect: "9:16" },
    { id: "instagram_reel", label: "Reel", aspect: "9:16" },
  ],
  fb: [
    { id: "facebook_feed", label: "Feed", aspect: "1:1" },
    { id: "facebook_story", label: "Story", aspect: "9:16" },
    { id: "facebook_reel", label: "Reel", aspect: "9:16" },
  ],
  tt: [{ id: "tiktok_video", label: "Video", aspect: "9:16" }],
};

/**
 * Media-generation mode of the Generate Studio. Drives the PR2 media-gen API
 * (POST enqueues a job → poll → preview → regenerate). Lives
 * on the same /generate route as the text-draft flow; rendered when the user
 * picks the "Photo / Video" mode.
 *
 * Honesty contract upheld here:
 * - Vertex (Gemini/Veo) models are disabled with a real reason when
 *   billing.providers.vertex is not true (FAIL-CLOSED / null-safe).
 * - usageUnits are shown as INFORMATIONAL cost only; the client never sends a
 *   cost — the server pre-checks and the worker meters.
 * - A 402 payment_required from the server surfaces verbatim; nothing renders
 *   as "generated"/charged before the poll returns done.
 */
export function GenerateMediaPanel({
  ws,
  brand,
  platform,
}: {
  ws: string | undefined;
  brand: string | undefined;
  platform: string;
}) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  const billingQuery = useBilling(ws);
  const productsQuery = useProducts(ws, brand);
  const mediaQuery = useMedia(ws, brand);
  const createJob = useCreateMediaGenJob(ws, brand);
  const shotDirector = useGenerateShotPrompt(ws, brand);

  const [jobId, setJobId] = useState<string | undefined>();
  const jobQuery = useMediaGenJob(ws, brand, jobId);

  const [capability, setCapability] = useState<MediaGenCapability>("image");
  const [modelId, setModelId] = useState<string>("stable-image");
  const [shotMode, setShotMode] = useState<ShotMode>("composite");
  const [aspect, setAspect] = useState<MediaAspect>(
    () => PLACEMENTS[toPlatformKey(platform)]?.[0]?.aspect ?? "4:5",
  );
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [productImages, setProductImages] = useState<
    { handle: string; index: number }[]
  >([]);
  const [sourceMediaIds, setSourceMediaIds] = useState<string[]>([]);
  // Which product's image-picker sheet is open (null = closed).
  const [sheetHandle, setSheetHandle] = useState<string | null>(null);
  // Post-type / placement (null = "manual" — the user picked an aspect that no
  // placement matches). Drives aspect + the shot director's composition hint.
  const pkey = toPlatformKey(platform);
  const placements = PLACEMENTS[pkey] ?? [];
  const [placementId, setPlacementId] = useState<string | null>(
    () => PLACEMENTS[toPlatformKey(platform)]?.[0]?.id ?? null,
  );

  // FAIL-CLOSED: Vertex is available only when the deployment reports it
  // configured. Absent/loading/false → treat as unconfigured so the UI never
  // offers a model the worker would reject.
  const vertexAvailable = billingQuery.data?.providers?.vertex === true;

  const models = useMemo(
    () => MEDIA_MODELS.filter((m) => m.capability === capability),
    [capability],
  );
  const selectedModel = MEDIA_MODELS.find((m) => m.id === modelId);

  // When the selected model isn't valid for the current capability (e.g. after
  // switching image→video), fall back to the first available model for it.
  const effectiveModel =
    selectedModel && selectedModel.capability === capability
      ? selectedModel
      : models.find((m) => m.provider === "bedrock") ?? models[0];

  // Shot modes the chosen model supports (video has no composite).
  const allowedShotModes = effectiveModel?.shotModes ?? [];
  const effectiveShotMode = allowedShotModes.includes(shotMode)
    ? shotMode
    : allowedShotModes[0];

  const job = jobQuery.data;
  const running =
    !!jobId &&
    (createJob.isPending ||
      job?.status === "queued" ||
      job?.status === "running" ||
      (!!jobId && !job && !jobQuery.isError));

  const modelUsable =
    !!effectiveModel && (effectiveModel.provider !== "vertex" || vertexAvailable);
  const canSubmit = !!prompt.trim() && modelUsable && !running;

  function onCapabilityChange(next: MediaGenCapability) {
    setCapability(next);
    const firstBedrock =
      MEDIA_MODELS.find((m) => m.capability === next && m.provider === "bedrock") ??
      MEDIA_MODELS.find((m) => m.capability === next);
    if (firstBedrock) setModelId(firstBedrock.id);
  }

  // Unique product handles for the shot director (which features products by
  // handle, not by specific image index).
  const productHandles = useMemo(
    () => [...new Set(productImages.map((p) => p.handle))],
    [productImages],
  );

  // Overrides let the one-click "Write & generate" path pass the freshly
  // generated prompt straight through, instead of relying on a setState flush
  // (React batches state, so reading `prompt` right after setPrompt is stale).
  async function onGenerate(overrides?: { prompt?: string; negativePrompt?: string }) {
    const effPrompt = (overrides?.prompt ?? prompt).trim();
    const effNeg = (overrides?.negativePrompt ?? negativePrompt).trim();
    if (!effPrompt || !modelUsable || running || !effectiveModel || !effectiveShotMode) return;
    const res = await createJob.mutateAsync({
      capability,
      modelId: effectiveModel.id,
      shotMode: effectiveShotMode,
      prompt: effPrompt,
      ...(effNeg ? { negativePrompt: effNeg } : {}),
      aspect,
      ...(productImages.length ? { productImages } : {}),
      ...(sourceMediaIds.length ? { sourceMediaIds } : {}),
    });
    setJobId(res.jobId);
  }

  /** AI shot director: write a prompt from the brand + product + the user's
   *  current text (used as an optional idea). Fills the boxes for review.
   *  Returns the result so the one-click path can generate without a flush. */
  async function writeShot() {
    if (!effectiveShotMode) return null;
    try {
      const res = await shotDirector.mutateAsync({
        capability,
        shotMode: effectiveShotMode,
        platform, // the long name ("instagram"/…) the endpoint validates against
        aspect,
        ...(placementId ? { postType: placementId } : {}),
        ...(prompt.trim() ? { idea: prompt.trim() } : {}),
        ...(productHandles.length ? { productHandles } : {}),
      });
      setPrompt(res.prompt);
      setNegativePrompt(res.negativePrompt);
      return res;
    } catch {
      return null; // surfaced via shotDirector.error in the UI
    }
  }

  async function writeAndGenerate() {
    const res = await writeShot();
    if (res) {
      await onGenerate({ prompt: res.prompt, negativePrompt: res.negativePrompt });
    }
  }

  /** Pick a placement → set its frame (aspect) + remember it for the shot director. */
  function selectPlacement(p: Placement) {
    setPlacementId(p.id);
    setAspect(p.aspect);
  }

  /** Manual aspect change. If it diverges from the active placement, drop the
   *  placement so the UI never shows e.g. "Reel" highlighted next to a 1:1 frame. */
  function onAspectChange(a: MediaAspect) {
    setAspect(a);
    const active = placements.find((p) => p.id === placementId);
    if (active && active.aspect !== a) setPlacementId(null);
  }

  // Resolve the finished asset(s) to presigned preview URLs from the media
  // list (the worker writes generated assets as MediaRecords). We refetch the
  // media list on completion so the new asset is present.
  const resultMedia = useMemo(() => {
    if (job?.status !== "done") return [];
    const byId = new Map(
      (mediaQuery.data?.media ?? []).map((m) => [mediaId(m), m]),
    );
    return (job.resultMediaIds ?? [])
      .map((id) => byId.get(id))
      .filter((m): m is NonNullable<typeof m> => !!m);
  }, [job, mediaQuery.data]);

  // When a job completes, refresh the media list once so the freshly-written
  // generated MediaRecords (and their presigned URLs) are present to render.
  // Keyed on the job + its result ids so it fires on completion, not every
  // render (an invalidate in the render body would loop).
  const doneJobId = job?.status === "done" ? job.jobId : undefined;
  const resultKey = job?.status === "done" ? job.resultMediaIds.join(",") : "";
  useEffect(() => {
    if (doneJobId && resultKey) {
      void qc.invalidateQueries({ queryKey: ["media", ws, brand] });
    }
  }, [doneJobId, resultKey, qc, ws, brand]);

  // When the studio's platform changes, keep the placement valid for it: if the
  // selected placement isn't offered by the new platform, snap to its first
  // placement and matching aspect (so postType + aspect never disagree). A
  // null placement (manual aspect) is left alone.
  useEffect(() => {
    if (placementId !== null && !placements.some((p) => p.id === placementId)) {
      const first = placements[0] ?? null;
      setPlacementId(first?.id ?? null);
      if (first) setAspect(first.aspect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkey]);

  const products = productsQuery.data?.products ?? [];
  const uploadedMedia = (mediaQuery.data?.media ?? []).filter(
    (m) => (m.origin ?? "uploaded") === "uploaded",
  );

  // Reference cap mirrors the worker's MAX_REFERENCES. Product images +
  // uploaded media share the budget; the worker drops anything past it, so we
  // block selection at the cap to keep the UI honest.
  const REF_CAP = 4;
  const totalRefs = productImages.length + sourceMediaIds.length;
  const atRefCap = totalRefs >= REF_CAP;
  const imagesForHandle = (handle: string) =>
    productImages.filter((pi) => pi.handle === handle).length;
  const isImageOn = (handle: string, index: number) =>
    productImages.some((pi) => pi.handle === handle && pi.index === index);
  function toggleProductImage(handle: string, index: number) {
    setProductImages((s) => {
      const exists = s.some((pi) => pi.handle === handle && pi.index === index);
      if (exists) {
        return s.filter((pi) => !(pi.handle === handle && pi.index === index));
      }
      if (s.length + sourceMediaIds.length >= REF_CAP) return s; // at cap
      return [...s, { handle, index }];
    });
  }
  function toggleSourceMedia(id: string) {
    setSourceMediaIds((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length + productImages.length >= REF_CAP) return s; // at cap
      return [...s, id];
    });
  }
  const sheetProduct = products.find((p) => p.handle === sheetHandle);

  // Close the image sheet on Escape.
  useEffect(() => {
    if (!sheetHandle) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSheetHandle(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheetHandle]);

  return (
    <div className={shared.layout}>
      {/* Request form */}
      <div className={shared.form}>
        {/* Capability */}
        <Field label="Generate">
          <div className={styles.capRow}>
            {CAPABILITIES.map((c) => (
              <button
                key={c.value}
                type="button"
                className={`${shared.chip} ${capability === c.value ? shared.chipOn : ""}`}
                onClick={() => onCapabilityChange(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Model toggle */}
        <Field
          label="Model"
          hint="Premium models cost more usage units per generation."
        >
          <div className={styles.modelList}>
            {models.map((m) => {
              const disabled = m.provider === "vertex" && !vertexAvailable;
              const on = effectiveModel?.id === m.id && !disabled;
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`${styles.modelOption} ${on ? styles.modelOptionOn : ""}`}
                  onClick={() => !disabled && setModelId(m.id)}
                  disabled={disabled}
                  aria-pressed={on}
                >
                  <span className={styles.modelMain}>
                    <span className={styles.modelLabel}>{m.label}</span>
                    {disabled ? (
                      <span className={styles.modelDisabledReason}>
                        {billingQuery.isLoading
                          ? "Checking availability…"
                          : "Not enabled on this deployment"}
                      </span>
                    ) : (
                      <span className={styles.modelMeta}>
                        {m.usageUnits} unit{m.usageUnits === 1 ? "" : "s"} ·{" "}
                        {m.capability}
                      </span>
                    )}
                  </span>
                  <span
                    className={`${styles.tierBadge} ${m.tier === "premium" ? styles.tierPremium : ""}`}
                  >
                    {m.tier}
                  </span>
                </button>
              );
            })}
          </div>
        </Field>

        {/* Shot mode */}
        <Field label="Shot mode" hint={SHOT_MODES.find((s) => s.value === effectiveShotMode)?.hint}>
          <div className={shared.chips}>
            {SHOT_MODES.filter((s) => allowedShotModes.includes(s.value)).map((s) => (
              <button
                key={s.value}
                type="button"
                className={`${shared.chip} ${effectiveShotMode === s.value ? shared.chipOn : ""}`}
                onClick={() => setShotMode(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Post type / placement */}
        {placements.length > 0 && (
          <Field
            label="Post type"
            hint="Sets the frame and tailors the shot to where it’ll run."
          >
            <div className={styles.aspectRow}>
              {placements.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`${shared.chip} ${placementId === p.id ? shared.chipOn : ""}`}
                  onClick={() => selectPlacement(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Field>
        )}

        {/* Aspect ratio */}
        <Field label="Aspect ratio">
          <div className={styles.aspectRow}>
            {ASPECTS.map((a) => (
              <button
                key={a}
                type="button"
                className={`${shared.chip} ${aspect === a ? shared.chipOn : ""}`}
                onClick={() => onAspectChange(a)}
              >
                {a}
              </button>
            ))}
          </div>
        </Field>

        {/* Prompt */}
        <Field label="Describe the shot" htmlFor="mg-prompt">
          <Textarea
            id="mg-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A sunlit flat-lay of the product on linen, warm tones, shallow depth of field…"
            rows={4}
          />
          <div className={styles.shotDirector}>
            <Btn
              size="sm"
              variant="secondary"
              onClick={() => void writeShot()}
              loading={shotDirector.isPending}
              disabled={running}
              iconLeft={<Icon name="sparkles" size={13} />}
            >
              Write the shot for me
            </Btn>
            <Btn
              size="sm"
              variant="ghost"
              onClick={() => void writeAndGenerate()}
              disabled={shotDirector.isPending || running || !modelUsable}
              iconLeft={<Icon name="zap" size={13} />}
            >
              Write &amp; generate
            </Btn>
          </div>
          <p className={styles.shotHint}>
            {productHandles.length
              ? "AI writes an on-brand shot from your selected product, its details, and anything you've typed above."
              : "AI writes an on-brand shot from your brand and anything you've typed above — no product needed."}{" "}
            Review it, or use “Write &amp; generate” to do it in one click.
          </p>
          {shotDirector.error && (
            <p className={shared.error}>{shotDirector.error.message}</p>
          )}
        </Field>
        <Field
          label="Avoid (optional)"
          htmlFor="mg-negative"
          hint="Things to keep out of the shot."
        >
          <Textarea
            id="mg-negative"
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
            placeholder="text, watermark, extra hands, cluttered background…"
            rows={2}
          />
        </Field>

        {/* Product picker */}
        <Field
          label="Feature products"
          hint="Tap a product to choose which of its photos to feature — pick more than one for richer shots."
        >
          {products.length === 0 ? (
            <p className={styles.pickerEmpty}>
              No synced products. Sync a product source or add products to use them here.
            </p>
          ) : (
            <>
              <div className={styles.pickerGrid}>
                {products.map((p) => {
                  const count = imagesForHandle(p.handle);
                  const on = count > 0;
                  const thumb = p.sourceImageUrls[0];
                  return (
                    <button
                      key={p.handle}
                      type="button"
                      className={`${styles.pickItem} ${on ? styles.pickItemOn : ""}`}
                      onClick={() => setSheetHandle(p.handle)}
                      aria-haspopup="dialog"
                      title={p.title}
                    >
                      {thumb ? (
                        <img className={styles.pickThumb} src={thumb} alt={p.title} loading="lazy" />
                      ) : (
                        <span className={styles.pickThumbFallback}>
                          <Icon name="image" size={18} />
                        </span>
                      )}
                      <span className={styles.pickLabel}>{p.title}</span>
                      {count > 0 && <span className={styles.pickBadge}>{count}</span>}
                    </button>
                  );
                })}
              </div>
              <span className={styles.pickerCount}>
                {totalRefs} of {REF_CAP} reference images selected
              </span>
            </>
          )}
        </Field>

        {/* Media reference picker */}
        <Field
          label="Reference media"
          hint="Your uploaded images used as references/inputs."
        >
          {uploadedMedia.length === 0 ? (
            <p className={styles.pickerEmpty}>
              No uploaded media yet. Add images in the Media library to reference them.
            </p>
          ) : (
            <>
              <div className={styles.pickerGrid}>
                {uploadedMedia.map((m) => {
                  const id = mediaId(m);
                  const on = sourceMediaIds.includes(id);
                  const isImg = m.mime.startsWith("image/");
                  const blocked = !on && atRefCap;
                  return (
                    <button
                      key={m.sk}
                      type="button"
                      className={`${styles.pickItem} ${on ? styles.pickItemOn : ""}`}
                      onClick={() => toggleSourceMedia(id)}
                      aria-pressed={on}
                      disabled={blocked}
                      title={
                        blocked
                          ? `Up to ${REF_CAP} reference images total`
                          : (m.context ?? m.purpose)
                      }
                    >
                      {m.url && isImg ? (
                        <img className={styles.pickThumb} src={m.url} alt={m.context ?? ""} loading="lazy" />
                      ) : (
                        <span className={styles.pickThumbFallback}>
                          <Icon name={isImg ? "image" : "play"} size={18} />
                        </span>
                      )}
                      <span className={styles.pickLabel}>{m.context ?? m.purpose}</span>
                      {on && (
                        <span className={styles.pickCheck}>
                          <Icon name="check" size={11} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {sourceMediaIds.length > 0 && (
                <span className={styles.pickerCount}>
                  {sourceMediaIds.length} selected
                </span>
              )}
            </>
          )}
        </Field>

        {/* Informational cost */}
        {effectiveModel && (
          <div className={styles.costLine}>
            <span>
              Cost: <span className={styles.costUnits}>{effectiveModel.usageUnits}</span> unit
              {effectiveModel.usageUnits === 1 ? "" : "s"}
            </span>
            <span className={styles.costNote}>
              counted against your plan usage
            </span>
          </div>
        )}

        {createJob.error && <p className={shared.error}>{createJob.error.message}</p>}

        <Btn
          variant="primary"
          full
          size="lg"
          onClick={() => onGenerate()}
          disabled={!canSubmit}
          loading={running}
          iconRight={<Icon name="zap" size={14} />}
        >
          {running ? "Generating…" : `Generate ${capability}`}
        </Btn>
      </div>

      {/* Results */}
      <div className={shared.results}>
        {!jobId && (
          <div className={shared.placeholder}>
            <Icon name="image" size={28} color="var(--text-4)" />
            <p>Your generated {capability} will appear here.</p>
          </div>
        )}

        {jobId && jobQuery.isError && (
          <div className={shared.failed}>
            <Icon name="info" size={16} color="var(--danger)" />
            <div>
              <strong>Lost track of that job.</strong>
              <p>{jobQuery.error?.message ?? "Couldn't load the job status."} Try generating again.</p>
            </div>
          </div>
        )}

        {running && (
          <div className={shared.status}>
            <span className={shared.spinner} aria-hidden="true" />
            <span>
              {job?.status === "running" ? "Rendering your shot…" : "Queued…"} Video can
              take a few minutes.
            </span>
          </div>
        )}

        {job?.status === "failed" && (
          <div className={shared.failed}>
            <Icon name="info" size={16} color="var(--danger)" />
            <div>
              <strong>Generation failed.</strong>
              <p>{job.error ?? "No asset was produced. You weren't charged for it."}</p>
            </div>
          </div>
        )}

        {job?.status === "done" && (
          <div>
            {resultMedia.length === 0 ? (
              <div className={shared.status}>
                <span className={shared.spinner} aria-hidden="true" />
                <span>Finalizing your asset…</span>
              </div>
            ) : (
              resultMedia.map((m) => {
                const spec: MediaSpec = {
                  type: capability === "video" ? "video" : "image",
                  aspect: aspect,
                  assetMime: m.mime,
                  ...(m.url ? { assetUrl: m.url } : {}),
                };
                return (
                  <div key={m.sk} className={styles.resultPreview}>
                    <MediaPreview platform={pkey} {...spec} width={isMobile ? "100%" : 420} />
                  </div>
                );
              })
            )}

            {resultMedia.length > 0 && (
              <p className={styles.attachedNote}>
                <Icon name="check" size={13} />
                Saved to your Media library — filter by “Generated”, or pick it as
                a reference in a future shot.
              </p>
            )}

            <div className={styles.resultActions}>
              <Btn
                variant="secondary"
                onClick={() => onGenerate()}
                disabled={!canSubmit}
                iconLeft={<Icon name="refresh" size={13} />}
              >
                Regenerate
              </Btn>
            </div>
          </div>
        )}
      </div>

      {/* Per-product image picker sheet — choose which of a product's photos to
          feature (multi-select, shares the reference cap). */}
      {sheetProduct && (
        <div
          className={styles.sheetBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={`Choose images for ${sheetProduct.title}`}
          onClick={() => setSheetHandle(null)}
        >
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sheetHead}>
              <span className={styles.sheetTitle}>{sheetProduct.title}</span>
              <button
                type="button"
                className={styles.sheetClose}
                onClick={() => setSheetHandle(null)}
                aria-label="Close"
              >
                <Icon name="x" size={16} />
              </button>
            </div>
            <p className={styles.sheetHint}>
              Pick the photos to feature — {totalRefs} of {REF_CAP} reference
              images selected.
            </p>
            {sheetProduct.sourceImageUrls.length === 0 ? (
              <p className={styles.pickerEmpty}>
                This product has no catalog images to choose from.
              </p>
            ) : (
              <div className={styles.sheetGrid}>
                {sheetProduct.sourceImageUrls.map((url, idx) => {
                  const on = isImageOn(sheetProduct.handle, idx);
                  const blocked = !on && atRefCap;
                  return (
                    <button
                      key={`${url}#${idx}`}
                      type="button"
                      className={`${styles.pickItem} ${on ? styles.pickItemOn : ""}`}
                      onClick={() => toggleProductImage(sheetProduct.handle, idx)}
                      aria-pressed={on}
                      disabled={blocked}
                      title={blocked ? `Up to ${REF_CAP} reference images total` : undefined}
                    >
                      <img
                        className={styles.pickThumb}
                        src={url}
                        alt={`${sheetProduct.title} ${idx + 1}`}
                        loading="lazy"
                      />
                      {on && (
                        <span className={styles.pickCheck}>
                          <Icon name="check" size={11} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <Btn variant="primary" full onClick={() => setSheetHandle(null)}>
              Done
              {imagesForHandle(sheetProduct.handle) > 0
                ? ` · ${imagesForHandle(sheetProduct.handle)} selected`
                : ""}
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}
